/**
 * Workspace slugs.
 *
 * A workspace's slug is its stable public identifier — shown in the
 * back-office today, and the label a workspace host would use if this
 * deployment ever gets wildcard DNS. That possibility is why the rules here
 * are hostname rules rather than "any URL-safe string": a slug that is not a
 * legal DNS label is a decision that gets expensive to undo once customers
 * hold it.
 */

/**
 * Names a workspace may not take, because something else already answers on
 * that host or would be confusing if it did.
 *
 * `www`, `api`, `app`, `media` and `mail` are real hosts in this deployment or
 * obvious future ones. The rest are reserved because a workspace called
 * "admin" or "billing" reads as ours rather than theirs — a phishing surface
 * handed out at signup.
 */
export const RESERVED_SLUGS: readonly string[] = [
  "admin",
  "api",
  "app",
  "assets",
  "auth",
  "billing",
  "blog",
  "cdn",
  "dashboard",
  "dev",
  "docs",
  "files",
  "help",
  "invite",
  "login",
  "mail",
  "media",
  "ns1",
  "ns2",
  "onboarding",
  "public",
  "relay",
  "root",
  "security",
  "signin",
  "signup",
  "smtp",
  "staging",
  "static",
  "status",
  "studio",
  "support",
  "test",
  "www",
];

/** Length bounds, shared by the generator and the validator. */
export const SLUG_MIN_LENGTH = 2;
export const SLUG_MAX_LENGTH = 60;

const SLUG_PATTERN = /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/;

/**
 * Whether a slug is usable as a hostname label.
 *
 * Stricter than "lowercase, digits and hyphens": a leading or trailing hyphen
 * is not a legal DNS label, and would produce a workspace whose host silently
 * fails to resolve rather than one that is merely ugly.
 */
export function isValidSlug(slug: string): boolean {
  if (slug.length < SLUG_MIN_LENGTH || slug.length > SLUG_MAX_LENGTH) return false;
  if (!SLUG_PATTERN.test(slug)) return false;
  return !RESERVED_SLUGS.includes(slug);
}

/**
 * A hostname label from a workspace name.
 *
 * Accents are folded rather than dropped, so "Café Noir" becomes `cafe-noir`
 * and not `caf-noir`. Anything left that is not a letter or digit becomes a
 * hyphen, runs collapse, and the ends are trimmed. A name made entirely of
 * characters that do not survive — a Chinese or emoji-only name — yields an
 * empty string, which the caller must handle rather than pretend about.
 */
export function slugify(name: string): string {
  const folded = name
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase();

  return folded
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, SLUG_MAX_LENGTH)
    .replace(/-+$/g, "");
}
